/**
 * Edits that add measures to a data tile, shared by the Measures tab and
 * the category editor: unique labels, appending measures, combining
 * categories into a net that stands in for them, and carrying a row's
 * dashboard colour to its new name. Pure.
 */

import {
  DataTile,
  Measure,
  NetMeasure,
  newId,
} from "features/Dashboard/types/dashboardDocument"

/** `label`, or `label (2)`, `label (3)` and on when `taken` holds it; claims it. */
export const uniqueLabel = (label: string, taken: Set<string>): string => {
  let out = label
  for (let n = 2; taken.has(out); n++) out = `${label} (${n})`
  taken.add(out)
  return out
}

/**
 * The dashboard's colours (by label) after a row or measure is renamed
 * from `from` to `to`: `to` gets the colour of `from` when `from` has one
 * and `to` has none. `from` keeps its colour, so other tiles still showing
 * that label keep theirs. Null when nothing changes.
 */
export const carryColour = (
  colours: Record<string, string>,
  from: string,
  to: string
): Record<string, string> | null => {
  if (from === to || !Object.hasOwn(colours, from) || Object.hasOwn(colours, to)) {
    return null
  }
  return { ...colours, [to]: colours[from] }
}

/** `ids` in the order of `categories`. */
export const inCategoryOrder = (
  categories: { id: string }[],
  ids: string[]
): string[] => categories.filter((c) => ids.includes(c.id)).map((c) => c.id)

/**
 * The category a KPI's row or a scorecard's source names, or null when the
 * tile is neither, picks nothing (its first row) or picks a measure.
 */
const pickedCategory = (tile: DataTile): string | null => {
  const pick =
    tile.type === "kpi"
      ? tile.visual.row
      : tile.type === "scorecard"
        ? tile.visual.source || null
        : null
  return pick !== null && !tile.measures.some((m) => m.id === pick)
    ? pick
    : null
}

/**
 * `tile` with `added` after its measures, labels made unique. A tile's
 * first measures switch it from its categories to its measures; a KPI or
 * scorecard that picks a category shows both instead, so its pick stays.
 */
export const appendMeasures = (tile: DataTile, added: Measure[]): DataTile => {
  const taken = new Set(tile.measures.map((m) => m.label))
  const fresh = added.map((m) => ({ ...m, label: uniqueLabel(m.label, taken) }))
  const first = tile.measures.length === 0 && tile.display.rows === "categories"
  const rows = pickedCategory(tile) !== null ? "both" : "measures"
  return {
    ...tile,
    measures: [...tile.measures, ...fresh],
    display: first ? { ...tile.display, rows } : tile.display,
  }
}

/**
 * `tile` with the NPS preset's measures after its own: the two nets, then
 * the score (see `appendMeasures`). A chart draws one unit, so on a bar,
 * line or pie tile the nets (percentages) are hidden and the chart draws
 * the score (points); a table, KPI or scorecard hides nothing.
 */
export const appendNps = (
  tile: DataTile,
  nets: [NetMeasure, NetMeasure],
  score: Measure
): DataTile => {
  const next = appendMeasures(tile, [...nets, score])
  if (tile.type !== "bar" && tile.type !== "line" && tile.type !== "pie") {
    return next
  }
  const { hidden } = next.categories
  const netIds = nets.map((n) => n.id).filter((id) => !hidden.includes(id))
  return {
    ...next,
    categories: { ...next.categories, hidden: [...hidden, ...netIds] },
  }
}

/**
 * `tile` with a net of `members` (row ids, in the order given) named
 * `label` (made unique) and the members hidden, so the net stands in for
 * them. A tile that showed only its categories shows both, so the other
 * categories stay; one that shows its measures keeps doing so. The net's
 * id is returned with the tile.
 */
export const combineIntoNet = (
  tile: DataTile,
  members: string[],
  label: string
): { tile: DataTile; netId: string } => {
  const net: NetMeasure = {
    id: newId("measure"),
    label: label.trim() || "Net",
    kind: "net",
    members: [...members],
  }
  const withNet = appendMeasures(tile, [net])
  const hidden = [
    ...tile.categories.hidden,
    ...members.filter((m) => !tile.categories.hidden.includes(m)),
  ]
  return {
    netId: net.id,
    tile: {
      ...withNet,
      display:
        tile.display.rows === "categories"
          ? { ...tile.display, rows: "both" }
          : withNet.display,
      categories: { ...tile.categories, hidden },
    },
  }
}
